'use client';

import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useState } from 'react';

import {
  formatHomepageCopy,
  homepagePath,
  type HomepageEditorialCopy,
} from '@/libs/homepage-editorial-copy';

import styles from './homepage-editorial.module.css';
import {
  productDescription,
  productName,
  type HomepageProduct,
} from './types';

const imageBaseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://aitoolly.com';

function screenshotUrl(value: string | null): string | null {
  if (!value) return null;
  return `${imageBaseUrl}/cdn-cgi/image/format=auto,width=720/${value}`;
}

function initials(value: string): string {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

export default function HomepageLatestTools({
  products,
  locale,
  copy,
}: {
  products: HomepageProduct[];
  locale: string;
  copy: HomepageEditorialCopy;
}) {
  const categories = useMemo(() => {
    const unique = Array.from(new Set(products.map((product) => product.category_name).filter(Boolean)));
    return unique.slice(0, 5);
  }, [products]);
  const [activeCategory, setActiveCategory] = useState(copy.all);

  const visibleProducts = activeCategory === copy.all
    ? products
    : products.filter((product) => product.category_name === activeCategory);

  if (products.length === 0) {
    return (
      <div className={styles.emptyState}>
        <h3>{copy.noToolsTitle}</h3>
        <p>{copy.noToolsDescription}</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.toolToolbar}>
        <div className={styles.filterButtons} aria-label={copy.categoriesTitle}>
          {[copy.all, ...categories].map((category) => (
            <button
              key={category}
              type="button"
              aria-pressed={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        <span className={styles.resultCount} aria-live="polite">
          {formatHomepageCopy(copy.toolsShown, { visible: visibleProducts.length, total: products.length })}
        </span>
      </div>

      {visibleProducts.length > 0 ? (
        <div className={styles.toolsGrid}>
          {visibleProducts.map((product) => {
            const name = productName(product);
            const image = screenshotUrl(product.screenshot);
            return (
              <Link
                key={product.id}
                className={styles.toolCard}
                href={homepagePath(locale, `/product/${product.slug}`)}
                prefetch={false}
              >
                <span className={styles.toolMedia} data-initials={initials(name)}>
                  {image && (
                    <Image
                      src={image}
                      alt={`${name} ${product.category_name} AI tool screenshot`}
                      fill
                      sizes="(max-width: 620px) 100vw, (max-width: 980px) 50vw, 25vw"
                      className={styles.toolImage}
                      unoptimized
                      onError={(event) => { event.currentTarget.style.display = 'none'; }}
                    />
                  )}
                </span>
                <span className={styles.toolContent}>
                  {product.category_name && <span className={styles.toolCategory}>{product.category_name}</span>}
                  <h3>{name}</h3>
                  <p>{productDescription(product)}</p>
                  <span className={styles.toolFooter}>
                    <span>{copy.productOverview}</span>
                    <strong>{copy.viewDetails} <ArrowRight size={15} aria-hidden="true" /></strong>
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <h3>{copy.noToolsTitle}</h3>
          <p>{copy.noToolsDescription}</p>
          <button type="button" onClick={() => setActiveCategory(copy.all)}>{copy.clearFilters}</button>
        </div>
      )}
    </>
  );
}
